import { Anchor, Badge, Button, Checkbox, Group, Stack, Text } from "@mantine/core"
import { useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { AuthModalButton, CenteredMessage, ErrorAlert, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { IconLogin2, appIcon } from "atlas_forecasting_app/components/common/icons"

function providerLabel(provider) {
  if (provider === "google_oauth2") return "Google"
  if (!provider) return "Unknown"
  return provider
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

export function SignupPage({ currentUser, authInfo, onSignupComplete, onAuthResult }) {
  const pendingSignup = authInfo?.pending_signup
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  if (currentUser) {
    return (
      <CenteredMessage
        eyebrow="Account"
        variant="editorial"
        title="Account ready"
        body="You are already signed in."
        action={
          <Button component={RouterLink} to="/feed">
            Go to feed
          </Button>
        }
      />
    )
  }

  if (!pendingSignup) {
    return (
      <CenteredMessage
        eyebrow="Signup"
        variant="editorial"
        title="Start with Google"
        body="Log in or create an account to continue."
        action={
          authInfo?.google_client_id ? (
            <AuthModalButton
              authInfo={authInfo}
              onAuthResult={onAuthResult}
              label="Sign up or Log in"
              modalTitle="Sign up or Log in"
              modalBody="Choose how you'd like to access Atlas Forecasting."
              leftSection={appIcon(IconLogin2, "inline")}
            />
          ) : (
            <Text c="red.8">Google sign-in is not configured (set GOOGLE_CLIENT_ID).</Text>
          )
        }
      />
    )
  }

  const submit = async () => {
    setSaving(true)
    setError("")

    try {
      const data = await apiFetch("/api/signup", {
        method: "POST",
        body: {
          accept_terms: acceptTerms,
          accept_privacy: acceptPrivacy,
        },
      })
      onSignupComplete(data.user, data.redirect_to || "/")
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Create account"
        variant="form"
        title="Complete signup"
        subtitle="Accept the Terms of Service and Privacy Policy to finish creating your Atlas Forecasting account."
      />

      <div className="af-form-layout">
        <SectionCard variant="utility" title="Account details" subtitle="Atlas will use this external identity for future sign-ins.">
          <Stack gap={6}>
            <Text>
              <Text span fw={600}>Provider:</Text> {providerLabel(pendingSignup.provider)}
            </Text>
            <Text>
              <Text span fw={600}>Email:</Text> {pendingSignup.email || "No email provided"}
            </Text>
          </Stack>
        </SectionCard>

        <SectionCard variant="form" title="Required agreements" subtitle="Atlas needs these acknowledgements before creating your account.">
          <Stack gap="md">
            <ErrorAlert message={error} />

            <Checkbox
              checked={acceptTerms}
              onChange={(event) => setAcceptTerms(event.currentTarget.checked)}
              label={(
                <Text>
                  I agree to the{" "}
                  <Anchor href="/terms" target="_blank" rel="noreferrer noopener">
                    Terms of Service
                  </Anchor>
                  .
                </Text>
              )}
            />

            <Checkbox
              checked={acceptPrivacy}
              onChange={(event) => setAcceptPrivacy(event.currentTarget.checked)}
              label={(
                <Text>
                  I acknowledge the{" "}
                  <Anchor href="/privacy" target="_blank" rel="noreferrer noopener">
                    Privacy Policy
                  </Anchor>
                  .
                </Text>
              )}
            />

            <Group gap="sm">
              <Button onClick={submit} loading={saving} disabled={!acceptTerms || !acceptPrivacy}>
                Create account
              </Button>
              {authInfo?.google_client_id ? (
                <AuthModalButton
                  authInfo={authInfo}
                  onAuthResult={onAuthResult}
                  label="Use a different Google account"
                  variant="subtle"
                  origin="/signup"
                  modalTitle="Choose account"
                  modalBody="Choose a Google account to replace the pending signup."
                  leftSection={appIcon(IconLogin2, "inline")}
                />
              ) : null}
            </Group>
          </Stack>
        </SectionCard>
      </div>
    </Stack>
  )
}
